import React from "react";
import Container from "../components/Container";
import Breadcrumb from "../components/Breadcrumb";
import { cls } from "../utils/helpers";
import contentFr from "./privacy_policy_fr"
import contentEn from "./privacy_policy_en"

function renderContent(content, key = 0) {
  if (typeof content === "string") {
    return content;
  }

  if (Array.isArray(content)) {
    return content.map((item, index) => renderContent(item, index));
  }

  if (typeof content === "object" && content !== null && content.type) {
    return <ContentBlock key={key} block={content} index={key} />;
  }

  return null;
}

function ContentBlock({ block, index }) {
  switch (block.type) {
    case "p":
      return (
        <p key={index} className="mb-4">
          {renderContent(block.content)}
        </p>
      );

    case "br":
      return (
        <br />
      );

    case "h1":
      return (
        <h1 key={index} className="text-3xl font-bold mb-4 mt-8">
          {renderContent(block.content)}
        </h1>
      );

    case "h2":
      return (
        <h2 key={index} className="text-2xl font-bold mb-3 mt-6">
          {renderContent(block.content)}
        </h2>
      );

    case "h3":
      return (
        <h3 key={index} className="text-xl font-semibold mb-3 mt-5">
          {renderContent(block.content)}
        </h3>
      );

    case "h4":
      return (
        <h4 key={index} className="text-lg font-semibold mb-2 mt-4">
          {renderContent(block.content)}
        </h4>
      );

    case "h5":
      return (
        <h5 key={index} className="text-base font-semibold mb-2 mt-3">
          {renderContent(block.content)}
        </h5>
      );

    case "h6":
      return (
        <h6 key={index} className="text-sm font-semibold mb-2 mt-3">
          {renderContent(block.content)}
        </h6>
      );

    case "small":
      return (
        <small key={index} className="text-sm text-gray-600 dark:text-gray-400 block mb-2">
          {renderContent(block.content)}
        </small>
      );

    case "span":
      return (
        <span key={index} className="inline">
          {renderContent(block.content)}
        </span>
      );

    case "b":
    case "strong":
      return (
        <strong key={index} className="font-bold">
          {renderContent(block.content)}
        </strong>
      );

    case "a":
    case "link":
      return (
        <a
          key={index}
          href={block.href || "#"}
          target={block.target || "_self"}
          rel={block.target === "_blank" ? "noopener noreferrer" : undefined}
          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline transition-colors"
        >
          {renderContent(block.content)}
        </a>
      );

    case "list_unordered":
      return (
        <ul key={index} className="list-disc list-inside mb-4 ml-4 space-y-2">
          {Array.isArray(block.content) &&
            block.content.map((item, i) => (
              <li key={i}>{renderContent(item)}</li>
            ))}
        </ul>
      );

    case "list_ordered":
      return (
        <ol key={index} className="list-decimal list-inside mb-4 ml-4 space-y-2">
          {Array.isArray(block.content) &&
            block.content.map((item, i) => (
              <li key={i}>{renderContent(item)}</li>
            ))}
        </ol>
      );

    case "table":
      return (
        <div key={index} className="overflow-x-auto mb-4">
          <table className="min-w-full border border-gray-300 dark:border-gray-600">
            <thead className="bg-gray-100 dark:bg-gray-700">
              <tr>
                {block.content.headers.map((header, i) => (
                  <th
                    key={i}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-left font-semibold"
                  >
                    {renderContent(header)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.content.rows.map((row, i) => (
                <tr key={i} className="bg-white dark:bg-gray-800">
                  <td className="px-4 py-3 border border-gray-300 dark:border-gray-600">
                    {renderContent(row.col1)}
                  </td>
                  <td className="px-4 py-3 border border-gray-300 dark:border-gray-600">
                    {renderContent(row.col2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    default:
      return null;
  }
}

export default function PrivacyPolicyPage({ p, t, lang = "fr" }) {
  const content = lang === "en" ? contentEn : contentFr;
  const title = lang === "en" ? "Privacy Policy" : "Politique de Confidentialité";
  const breadcrumbLabel = lang === "en" ? "Privacy Policy" : "Confidentialité";

  return (
    <>
      <Container>
        <Breadcrumb
          items={[
            { label: t("crumb.home"), href: "/" },
            { label: breadcrumbLabel },
          ]}
          p={p}
        />
      </Container>

      <section className={p.hero}>
        <Container>
          <div className="py-12">
            <h1 className={cls("text-3xl font-extrabold", p.cardTitle)}>
              {title}
            </h1>
          </div>
        </Container>
      </section>

      <section className="py-10">
        <Container>
          <div className="max-w-4xl mx-auto bg-white/70 dark:bg-gray-800/70 backdrop-blur rounded-2xl p-8 ring-1 ring-indigo-200 dark:ring-indigo-800">
            <div className={cls("space-y-8", p.paragraph)}>
              {content.sections.map((section, index) => (
                <div key={index}>
                  <h2 className="text-2xl font-bold mb-4">{section.title}</h2>
                  {section.content.map((block, blockIndex) => (
                    <ContentBlock key={blockIndex} block={block} index={blockIndex} />
                  ))}
                </div>
              ))}
              <p className="mt-8 text-sm text-gray-500">
                {content.lastUpdated}
              </p>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}
